import { useMemo } from "react";
import useUser from "~/contexts/user";
import { useFetcher } from "~/hooks/useFetcher";
import { projectDatasetsPath } from "~/services/projects";
import { contextSkillsPath, PartialSkill, SkillPage, systemSkillsPath } from "~/services/skills";

interface UseSkillsResult {
  skills: PartialSkill[];
  isLoading: boolean;
}

// Fetches the skills available to the current conversation: SYSTEM skills
// (baseline) plus the AGENT/DATASET skills tied to this conversation's context
// and the caller's own USER skills. Re-keys naturally when `agentId`,
// `projectId`, or the user changes, so switching conversations or projects
// refreshes the list.
export function useSkills(
  agentId: string | null | undefined,
  projectId: string | null | undefined
): UseSkillsResult {
  const user = useUser();

  const { data: datasetIds, isLoading: datasetsLoading } = useFetcher<string[]>(
    projectId ? projectDatasetsPath(projectId) : null
  );

  const { data: systemData, isLoading: systemLoading } = useFetcher<SkillPage>(systemSkillsPath());

  // Wait until project datasets resolve (or are absent) before issuing the
  // multi-scope query — otherwise we'd re-key mid-flight and waste a request.
  // Passing `user_id` ensures the caller's USER skills are always included,
  // even in a conversation with no agent or project.
  const contextKey =
    !projectId || !datasetsLoading
      ? contextSkillsPath({
          agent_id: agentId,
          dataset_ids: datasetIds,
          user_id: user?.email ?? null,
        })
      : null;

  const { data: contextData, isLoading: contextLoading } = useFetcher<SkillPage>(contextKey);

  const skills = useMemo(() => {
    const byName = new Map<string, PartialSkill>();
    for (const s of systemData?.items ?? []) byName.set(s.name, s);
    // Context items intentionally overwrite SYSTEM entries with the same
    // name, mirroring the backend's USER > DATASET > SYSTEM precedence.
    for (const s of contextData?.items ?? []) byName.set(s.name, s);
    return Array.from(byName.values()).sort((a, b) => a.name.localeCompare(b.name));
  }, [systemData, contextData]);

  return {
    skills,
    isLoading: systemLoading || datasetsLoading || contextLoading,
  };
}
